import { Injectable } from '@angular/core';

import { PedidoItem } from '../pedido-item';
import { toFixed } from '../../../../core/services/utils/numbers';
import { PedidoItemValorUnitarioQuantidade } from '../valor-unitario-quantidade';

@Injectable({
    providedIn: 'root'
})
export class PedidoItemCalculoService {

     /** Calcula o valor bruto (sem desconto) dos itens do pedido */
     calcularValorBruto(quantidade: number, valorUnitario: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorUnitario = valorUnitario && !isNaN(valorUnitario) ? Number(valorUnitario) : 0;

        const valorBruto = quantidade * valorUnitario;
        return toFixed(valorBruto, 2);
    }


    /** Calcula o valor líquido dos itens do pedido */
    calcularValorLiquido(valorUnitario: number, percDesconto1: number, percDesconto2: number): number {
        valorUnitario = valorUnitario && !isNaN(valorUnitario) ? Number(valorUnitario) : 0;
        percDesconto1 = percDesconto1 && !isNaN(percDesconto1) ? Number(percDesconto1) : 0;
        percDesconto2 = percDesconto2 && !isNaN(percDesconto2) ? Number(percDesconto2) : 0;

        // Desconto 1
        let valorLiquido = valorUnitario - ((valorUnitario * percDesconto1) / 100);
        // Desconto 2 (Em cima do desconto 1)
        valorLiquido = valorLiquido - ((valorLiquido * percDesconto2) / 100);

        return toFixed(valorLiquido, 6);
    }

    /** Calcula o valor líquido dos itens do pedido considerando também o desconto à vista */
    calcularValorLiquidoComDescontoAvista(valorUnitario: number, percDesconto1: number, percDesconto2: number, percDescontoAvista: number, valorUntiarioFrete = 0, casasDecimais = 6): number {
        valorUnitario = valorUnitario && !isNaN(valorUnitario) ? Number(valorUnitario) : 0;
        percDesconto1 = percDesconto1 && !isNaN(percDesconto1) ? Number(percDesconto1) : 0;
        percDesconto2 = percDesconto2 && !isNaN(percDesconto2) ? Number(percDesconto2) : 0;
        percDescontoAvista = percDescontoAvista && !isNaN(percDescontoAvista) ? Number(percDescontoAvista) : 0;
        valorUntiarioFrete = valorUntiarioFrete && !isNaN(valorUntiarioFrete) ? Number(valorUntiarioFrete) : 0;

        // Desconto 1
        let valorLiquido = valorUnitario - ((valorUnitario * percDesconto1) / 100);
        // Desconto 2 (Em cima do desconto 1)
        valorLiquido = valorLiquido - ((valorLiquido * percDesconto2) / 100);
        // Desconto à vista
        valorLiquido = valorLiquido - ((valorLiquido * percDescontoAvista) / 100)
        // Valor Unitário do frete
        valorLiquido = valorLiquido + valorUntiarioFrete;

        return toFixed(valorLiquido, casasDecimais);
    }

    /** Calcula o coeficiente dos itens do pedido */
    calcularCoeficiente(valorUnitario: number, valorLiquido: number): number {
        valorUnitario = valorUnitario && !isNaN(valorUnitario) ? Number(valorUnitario) : 0;
        valorLiquido = valorLiquido && !isNaN(valorLiquido) ? Number(valorLiquido) : 0;

        if (valorUnitario === 0) {
            return 0;
        }

        const coeficiente  = valorLiquido / valorUnitario;
        return toFixed(coeficiente, 5);
    }

     /** Calcula o valor unitário através do valor unitário da moeda multiplicado pela taxa de câmbio informada */
     calcularValorUnitarioPelaTaxaCambio(valorUnitarioMoeda: number, taxaCambio: number): number {
        valorUnitarioMoeda = valorUnitarioMoeda && !isNaN(valorUnitarioMoeda) ? Number(valorUnitarioMoeda) : 0;
         taxaCambio = taxaCambio && !isNaN(taxaCambio) ? Number(taxaCambio) : 0;

        const valorUnitario = valorUnitarioMoeda * taxaCambio;
        return toFixed(valorUnitario, 4);
    }

     /** Calcula o valor unitário da moeda através do valor unitário divido pela taxa de câmbio informada */
     calcularValorUnitarioMoedaPelaTaxaCambio(valorUnitario: number, taxaCambio: number): number {
        valorUnitario = valorUnitario && !isNaN(valorUnitario) ? Number(valorUnitario) : 0;
        taxaCambio = taxaCambio && !isNaN(taxaCambio) ? Number(taxaCambio) : 0;
        if (taxaCambio === 0) {
            return 0;
        }

        const valorUnitarioMoeda = valorUnitario / taxaCambio;
        return toFixed(valorUnitarioMoeda, 6);
    }

     /** Calcula o valor total dos itens do pedido */
    calcularValorTotal(quantidade, valorLiquido: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorLiquido = valorLiquido && !isNaN(valorLiquido) ? Number(valorLiquido) : 0;

        const valorTotal = quantidade * valorLiquido;
        return toFixed(valorTotal, 2);
    }

     /** Calcula o valor total dos itens do pedido na moeda do pedido */
     calcularValorTotalMoeda(quantidade, valorLiquido: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorLiquido = valorLiquido && !isNaN(valorLiquido) ? Number(valorLiquido) : 0;

        const valorTotal = quantidade * valorLiquido;
        return toFixed(valorTotal, 4);
    }

    /** Calcula o valor total geral de todos itens informados */
    calcularValorTotalGeral(entities: PedidoItem[]): number {
        if (!entities) {
            return 0;
        }

        let valorTotal = 0;
        entities.forEach(entity => {
            valorTotal += entity.valorTotal ? entity.valorTotal : 0;
        });

        return toFixed(valorTotal, 2);
    }

    /** Calcula o valor total geral de todos os grupos da listagem informados na moeda do pedido */
    calcularValorTotalGeralMoeda(entities: PedidoItem[]): number {
        if (!entities) {
            return 0;
        }

        let valorTotalMoeda = 0;
        entities.forEach(entity => {
            valorTotalMoeda += entity.valorTotalMoeda ? entity.valorTotalMoeda : 0;
        });

        return toFixed(valorTotalMoeda, 4);
    }

    /** Calcula o desconto médio dos itens do pedido */
    calcularPercDescontoMedio(entities: PedidoItem[], percDescontoAvista: number): number {
        if (!entities) {
            return 0;
        }

        let valorTotalSemDesconto = 0;
        let valorTotalComDesconto = 0;
        entities.forEach(entity => {
            // Total Sem Desconto
            const valorBruto = this.calcularValorBruto(entity.quantidade, entity.valorUnitario);
            valorTotalSemDesconto += valorBruto;
            // Total Com Desconto
            // valorTotalComDesconto += entity.valorTotal;
            const valorLiquido = this.calcularValorLiquidoComDescontoAvista(entity.valorUnitario, entity.percDesconto1, entity.percDesconto2, percDescontoAvista)
            const valorTotal = this.calcularValorTotal(entity.quantidade, valorLiquido);
            valorTotalComDesconto += valorTotal;
        });

        // Se não tiver valor retornamos 0
        if (valorTotalSemDesconto <= 0) {
            return 0;
        }
        valorTotalComDesconto = toFixed(valorTotalComDesconto, 2);
        valorTotalSemDesconto = toFixed(valorTotalSemDesconto, 2);

        // Fórmula do desconto médio
        const percDescontoMedio = 100 - ((valorTotalComDesconto / valorTotalSemDesconto) * 100);
        return toFixed(percDescontoMedio, 2);
    }

    /** Calcula o valor total presente dos itens do pedido.
    Este valor é calculado através do valor total do item - uma porcentagem estabelecida pela empresa */
    calcularValorTotalPresente(valorTotal: number, percValorPresente: number): number {
        valorTotal = valorTotal && !isNaN(valorTotal) ? Number(valorTotal) : 0;
        percValorPresente = percValorPresente && !isNaN(percValorPresente) ? Number(percValorPresente) : 0;

        // 1 - Achando o valor a ser descontado do valor total com base no percentual do valor presente
        const valorDescontar = (valorTotal * percValorPresente) / 100;
        // 2 - Diminuindo do valor total do item o valor o valor referente ao valor presente calculado anteriormente
        const valorTotalPresente = valorTotal - valorDescontar;

        return toFixed(valorTotalPresente, 2);
    }

    /** Calcula o valor da DVV dos itens do pedido */
    calcularValorDvv(valorTotal: number, percDvv: number, percComissaoItem: number): number {
        valorTotal = valorTotal && !isNaN(valorTotal) ? Number(valorTotal) : 0;
        percDvv = percDvv && !isNaN(percDvv) ? Number(percDvv) : 0;
        percComissaoItem = percComissaoItem && !isNaN(percComissaoItem) ? Number(percComissaoItem) : 0;

        // Primeiro somamos o percentual da comissão do item ao percentual da DVV
        percDvv += percComissaoItem

        const valorDVV = (valorTotal * percDvv) / 100;
        return toFixed(valorDVV, 4);
    }

    /** Calcula o custo variável total dos itens do pedido */
    calcularCustoVariavelTotal(quantidade: number, custoVariavelUnitario: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        custoVariavelUnitario = custoVariavelUnitario && !isNaN(custoVariavelUnitario) ? Number(custoVariavelUnitario) : 0;

        const custoVariavelTotal = quantidade * custoVariavelUnitario;
        return toFixed(custoVariavelTotal, 4);
    }

    /** Calcula o custo fixo total dos itens do pedido **/
    calcularCustoFixoTotal(quantidade: number, custoFixoUnitario: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        custoFixoUnitario = custoFixoUnitario && !isNaN(custoFixoUnitario) ? Number(custoFixoUnitario) : 0;

        const custoFixoTotal = quantidade * custoFixoUnitario;
        return toFixed(custoFixoTotal, 4);
    }

    /** Calcula o custo produção total dos itens do pedido **/
    calcularCustoProducaoTotal(quantidade: number, custoProducaoUnitario: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        custoProducaoUnitario = custoProducaoUnitario && !isNaN(custoProducaoUnitario) ? Number(custoProducaoUnitario) : 0;

        const custoProducaoTotal = quantidade * custoProducaoUnitario;
        return toFixed(custoProducaoTotal, 4);
    }

    /** Calcula o valor total dos royalties dos itens do pedido **/
    calcularRoyaltiesTotal(valorTotalPresente: number, percRoyalties: number): number {
        valorTotalPresente = valorTotalPresente && !isNaN(valorTotalPresente) ? Number(valorTotalPresente) : 0;
        percRoyalties = percRoyalties && !isNaN(percRoyalties) ? Number(percRoyalties) : 0;

        const valorRoyalties = (valorTotalPresente * percRoyalties) / 100
        return toFixed(valorRoyalties, 4);
    }

    getValorUnitarioPelaQuantidade(quantidade: number, variacoes: PedidoItemValorUnitarioQuantidade[]): number {
        if (quantidade == null || quantidade === undefined) {
            return 0;
        }
        if (!variacoes) {
            return 0;
        }
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;

        for (let i = 0; i < variacoes.length; i++) {
            const variacao = variacoes[i];

            if ( (variacao.quantidadeInicial != null && variacao.quantidadeInicial !== undefined && quantidade >= variacao.quantidadeInicial) &&
                 (variacao.quantidadeFinal == null || variacao.quantidadeFinal === undefined || quantidade <= variacao.quantidadeFinal) ) {
                return variacao.valorUnitario;
            }
        }

        return 0;
    }

    /** Calcula o valor unitário do frete dividindo (rateando) o valor total do frete pela quantidade */
    calcularValorUnitarioFrete(quantidade: number, valorTotalFrete: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorTotalFrete = valorTotalFrete && !isNaN(valorTotalFrete) ? Number(valorTotalFrete) : 0;
        if (quantidade === 0 ) {
            return 0;
        }

        const valorUnitarioFrete = valorTotalFrete / quantidade;
        return toFixed(valorUnitarioFrete, 4);
    }

    /** Calcula o valor unitário do frete através do valor unitário do frete na moeda do pedido multiplicado pela taxa de câmbio informada */
    calcularValorUnitarioFretePelaTaxaCambio(valorUnitarioFreteMoeda: number, taxaCambio: number): number {
        valorUnitarioFreteMoeda = valorUnitarioFreteMoeda && !isNaN(valorUnitarioFreteMoeda) ? Number(valorUnitarioFreteMoeda) : 0;
        taxaCambio = taxaCambio && !isNaN(taxaCambio) ? Number(taxaCambio) : 0;

        const valorUnitario = valorUnitarioFreteMoeda * taxaCambio;
        return toFixed(valorUnitario, 4);
    }

    /** Calcula o valor total do frete */
    calcularValorTotalFrete(quantidade, valorUnitarioFrete: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorUnitarioFrete = valorUnitarioFrete && !isNaN(valorUnitarioFrete) ? Number(valorUnitarioFrete) : 0;

        const valorTotalFrete = quantidade * valorUnitarioFrete;
        return toFixed(valorTotalFrete, 2);
    }

    /** Calcula o valor unitário do frete na moeda do pedido dividindo (rateando) o valor total do frete na moeda do pedido pela quantidade */
    calcularValorUnitarioFreteMoeda(quantidade: number, valorTotalFreteMoeda: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorTotalFreteMoeda = valorTotalFreteMoeda && !isNaN(valorTotalFreteMoeda) ? Number(valorTotalFreteMoeda) : 0;
        if (quantidade === 0 ) {
            return 0;
        }

        const valorUnitarioFrete = valorTotalFreteMoeda / quantidade;
        return toFixed(valorUnitarioFrete, 6);
    }

     /** Calcula o valor unitário do frete da moeda através do valor unitário do frete divido pela taxa de câmbio informada */
     calcularValorUnitarioFreteMoedaPelaTaxaCambio(valorUnitariFrete: number, taxaCambio: number): number {
        valorUnitariFrete = valorUnitariFrete && !isNaN(valorUnitariFrete) ? Number(valorUnitariFrete) : 0;
        taxaCambio = taxaCambio && !isNaN(taxaCambio) ? Number(taxaCambio) : 0;
        if (taxaCambio === 0) {
            return 0;
        }

        const valorUnitariFreteMoeda = valorUnitariFrete / taxaCambio;
        return toFixed(valorUnitariFreteMoeda, 6);
    }

    /** Calcula o valor total do frete na moeda do pedido */
    calcularValorTotalFreteMoeda(quantidade, valorUnitarioFreteMoeda: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        valorUnitarioFreteMoeda = valorUnitarioFreteMoeda && !isNaN(valorUnitarioFreteMoeda) ? Number(valorUnitarioFreteMoeda) : 0;

        const valorTotalFreteMoeda = quantidade * valorUnitarioFreteMoeda;
        return toFixed(valorTotalFreteMoeda, 4);
    }

    /** Calcula o valor total do frete do pedido através dos valores totais de fretes de seus itens */
    calcularValorTotalFreteGeral(entities: PedidoItem[]): number {
        if (!entities) {
            return 0;
        }

        let valorTotalFrete = 0;
        entities.forEach(entity => {
            if (entity.quantidade > 0) { // Somente calculamos o frete dos itens que tiverem quantidade informada
                valorTotalFrete += entity.valorTotalFrete ? entity.valorTotalFrete : 0;
            }
        });

        return toFixed(valorTotalFrete, 2);
    }

    /** Calcula o valor total do frete na moeda do pedido através dos valores totais de fretes de seus itens */
    calcularValorTotalFreteGeralMoeda(entities: PedidoItem[]): number {
        if (!entities) {
            return 0;
        }

        let valorTotalFreteMoeda = 0;
        entities.forEach(entity => {
            if (entity.quantidade > 0) { // Somente calculamos o frete dos itens que tiverem quantidade informada
                valorTotalFreteMoeda += entity.valorTotalFreteMoeda ? entity.valorTotalFreteMoeda : 0;
            }
        });

        return toFixed(valorTotalFreteMoeda, 4);
    }

    /** Calcula a quantidade com acréscimo da tolerância do pedido */
    calcularQuantidadeTolerancia(quantidade: number, percQuantidadeToleranciaPedido: number): number {
        quantidade = quantidade && !isNaN(quantidade) ? Number(quantidade) : 0;
        percQuantidadeToleranciaPedido = percQuantidadeToleranciaPedido && !isNaN(percQuantidadeToleranciaPedido) ? Number(percQuantidadeToleranciaPedido) : 0;

        const quantidadeTolerancia = quantidade + ((quantidade * percQuantidadeToleranciaPedido) / 100);
        return toFixed(quantidadeTolerancia, 6);
    }
}
